import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { Dialog } from 'primereact/dialog';
import JsonView from '@uiw/react-json-view';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Strings from '../../../i18n';
import { viewRightSideBarEnum } from '../../../constants/sidebarEnums';
import { DeviceMethodsSideBarProps } from './deviceMethodsSideBarProps';
import {
  useDeleteDeviceCredentialsMutation,
  useDeviceRebootCommandMutation,
  useGetCredentialsMutation,
  useGetTwinInfoMutation,
  usePostCustomFileDeviceMutation,
  useRegenerateIotHubCredentialsMutation,
  useSendMultipleFilesMutation
} from '../../../services/device/deviceService';
import { SideBarSimpleMethodItem } from '../../../components/commons/SideBarComponents/SimpleMethodItem/sideBarSimpleMethodButtonItem';
import { SideBarDeleteActionWithPopup } from '../../../components/commons/SideBarComponents/DeleteActionWithPopup/sideBarDeleteActionWithPopup';
import { LoadingRowsComponent } from '../../../components/commons/LoadingRows/LoadingRows';
import { SideBarSimpleMethodWithValue } from '../../../components/commons/SideBarComponents/MethodWithValueItem/sideBarMethodWithValueItem';
import { JsonViewItem } from '../../../components/commons/JsonViewItem/jsonViewItem';
import {
  useDeleteDeviceCommandMutation,
  useDeleteDeviceFromLocationMutation,
  useSwapControllerMutation
} from '../../../services/deviceByLocation/deviceByLocationService';
import { SwapController } from '../swapController/swapController';

export const DeviceMethodsSideBar = ({
  openRightSideBarForMethod,
  deviceIds,
  reloadTable,
  setOpenRightSideBar,
  deviceLocationView,
  clearSingleDevice,
  idDevice,
  hasNotes,
  macAddress,
  generation,
  productType,
  isMultipleControllerOldGeneration
}: DeviceMethodsSideBarProps) => {
  const navigate = useNavigate();
  const isOldGeneration = generation ? generation <= 5 : false;
  const newGenerationController = 7;
  const [visibleCredentials, setVisible] = useState(false);
  const [visibleTwinInfo, setVisibleTwinInfo] = useState(false);
  const [fileUpload, setFileUpload] = useState<any>(null);
  const [swapController, setSwapControlelr] = useState<boolean>(false);
  const [
    getCredentials,
    {
      isLoading: isLoadingGetCredentials,
      isSuccess: isSuccessGetCredentials,
      data: dataGetCredentials
    }
  ] = useGetCredentialsMutation();
  const [
    getTwinInfo,
    {
      isLoading: isLoadingGetTwinInfo,
      isSuccess: isSuccessGetTwinInfo,
      data: dataGetTwinInfo
    }
  ] = useGetTwinInfoMutation();
  const [
    deleteCredentials,
    {
      isSuccess: isSuccessDeleteCredentials,
      isLoading: isLoadingDeleteCredentials
    }
  ] = useDeleteDeviceCredentialsMutation();

  const [postCustomFile, { isSuccess: isSuccessPostCustom }] =
    usePostCustomFileDeviceMutation();

  const [
    rebootDevicesCommand,
    {
      isSuccess: isSuccessRebootDevice,
      isError: isErrorRebootDevice,
      error: errorRebootDevice
    }
  ] = useDeviceRebootCommandMutation();

  const [
    deleteFromLocation,
    {
      isSuccess: isSuccessDeleteFromLocation,
      isLoading: isLoadingDeleteFromLocation
    }
  ] = useDeleteDeviceCommandMutation();

  const [hardDeleteDevice, { isSuccess: isSuccessDeleteDeviceFromLocation }] =
    useDeleteDeviceFromLocationMutation();

  const [
    regenerateIotHubCredentials,
    {
      isSuccess: isSuccessRegenerateIotHubCredentials,
      isLoading: isLoadingRegenerateIotHubCredentials
    }
  ] = useRegenerateIotHubCredentialsMutation();

  const [
    sendMultipleFiles,
    {
      isSuccess: isSuccessSendMultipleFiles,
      isLoading: isLoadingSendMultipleFiles
    }
  ] = useSendMultipleFilesMutation();

  const [
    swapControllerAction,
    {
      isSuccess: isSuccessSwapController,
      isError: isErrorSwapController,
      error: swapControllerError
    }
  ] = useSwapControllerMutation();

  useEffect(() => {
    if (isSuccessDeleteFromLocation || isSuccessDeleteDeviceFromLocation) {
      toast.success(Strings.serverAccess.devices.successDeletedDevice);
      reloadTable();
      setOpenRightSideBar(false);
    }
  }, [isSuccessDeleteFromLocation, isSuccessDeleteDeviceFromLocation]);

  useEffect(() => {
    if (isSuccessPostCustom) {
      toast.success(Strings.serverAccess.devices.successPostCustomFile);
      setFileUpload(null);
      setOpenRightSideBar(false);
    }
  }, [isSuccessPostCustom]);

  useEffect(() => {
    if (isSuccessRebootDevice) {
      toast.success(Strings.serverAccess.devices.successRebootDevice);
      reloadTable();
      setOpenRightSideBar(false);
    }
  }, [isSuccessRebootDevice]);

  useEffect(() => {
    if (isErrorRebootDevice) {
      toast.error(
        `${(errorRebootDevice as { data: { message: string } }).data.message}`
      );
    }
  }, [isErrorRebootDevice]);

  const deleteItem = (ids: string[]) => {
    deleteFromLocation(ids as unknown as number[]);
  };

  const changeFileHandler = (event: any) => {
    setFileUpload(event.target.files[0]);
  };

  const sendFile = () => {
    if (fileUpload) {
      const data = new FormData();
      data.append('file', fileUpload);
      postCustomFile({ deviceId: deviceIds[0], data });
    }
  };

  const rebootDevices = (ids: string[]) => {
    rebootDevicesCommand(ids);
  };

  const getCredentialsAndOpenDialog = () => {
    setVisible(true);
    getCredentials(deviceIds[0]);
  };

  const getTwinInfoAndOpenDialog = () => {
    setVisibleTwinInfo(true);
    getTwinInfo(deviceIds[0]);
  };

  useEffect(() => {
    if (isSuccessDeleteCredentials) {
      toast.success(Strings.serverAccess.devices.successDeleteCredentials);
      reloadTable();
      setOpenRightSideBar(false);
    }
  }, [isSuccessDeleteCredentials]);

  useEffect(() => {
    if (isSuccessRegenerateIotHubCredentials) {
      toast.success(
        Strings.serverAccess.devices.successRegenerateIotHubCredentials
      );
      reloadTable();
      setOpenRightSideBar(false);
    }
  }, [isSuccessRegenerateIotHubCredentials]);

  const redirectToSeeNotes = () => {
    navigate('/my-notes', {
      state: {
        type: 'Devices',
        id: deviceIds[0]
      }
    });
  };

  const redirectToTestController = () => {
    navigate(`/testDevice/${deviceIds[0]}`, {
      state: { isOldGeneration }
    });
  };

  const handleSendMultipleFiles = () => {
    sendMultipleFiles({ deviceIds, fileNames: ['A:/AUDIT/Event_Log.txt'] });
  };

  useEffect(() => {
    if (isSuccessSendMultipleFiles) {
      toast.success(Strings.serverAccess.devices.successSendMultipleFiles);
      setOpenRightSideBar(false);
    }
  }, [isSuccessSendMultipleFiles]);

  const handleSwapController = (newControllerId: number) => {
    swapControllerAction({ oldControllerId: idDevice[0], newControllerId });
  };

  useEffect(() => {
    if (isSuccessSwapController) {
      setSwapControlelr(false);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccessSwapController]);

  useEffect(() => {
    if (
      isErrorSwapController &&
      swapControllerError &&
      'data' in swapControllerError
    ) {
      const errorData = swapControllerError.data as { message?: string };
      if (errorData && errorData.message) {
        toast.error(errorData.message);
      }
    }
  }, [isErrorSwapController, swapControllerError]);

  return (
    <>
      <Grid container>
        {swapController ? (
          <SwapController
            oldControllerId={idDevice[0]}
            productType={productType}
            onSave={handleSwapController}
            onCancel={() => {
              setSwapControlelr(false);
            }}
          />
        ) : (
          <>
            {deviceIds.length === 1 && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.testController}
                action={redirectToTestController}
                successAction={false}
                textSuccess="success"
              />
            )}
            {!isOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.singletonMethodLabel}
                action={() =>
                  openRightSideBarForMethod(
                    viewRightSideBarEnum.singletonDevice
                  )
                }
                successAction={false}
                textSuccess="success"
              />
            )}
            {!isOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.staticMethodLabel}
                action={() =>
                  openRightSideBarForMethod(viewRightSideBarEnum.staticDevice)
                }
                successAction={false}
                textSuccess="success"
              />
            )}
            {!deviceLocationView && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.addDeviceToLocationLabel}
                action={() => {
                  clearSingleDevice();
                  openRightSideBarForMethod(
                    viewRightSideBarEnum.addDeviceToLocation
                  );
                }}
                successAction={false}
                textSuccess="success"
              />
            )}
            {deviceIds.length === 1 && !isOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.getCredentials}
                action={() => getCredentialsAndOpenDialog()}
                successAction={false}
                textSuccess="success"
              />
            )}
            {deviceIds.length === 1 && (
              <SideBarSimpleMethodItem
                name={Strings.notes.addNote}
                action={() => {
                  openRightSideBarForMethod(viewRightSideBarEnum.addNote);
                }}
                successAction={false}
                textSuccess="success"
              />
            )}
            {deviceIds.length === 1 && hasNotes && (
              <SideBarSimpleMethodItem
                name={Strings.notes.seeNotes}
                action={redirectToSeeNotes}
                successAction={false}
                textSuccess="success"
              />
            )}
            {deviceIds.length === 1 && !isOldGeneration && (
              <SideBarSimpleMethodWithValue
                label="Custom File"
                type="inputFile"
                currentValue={fileUpload}
                onChange={(e) => changeFileHandler(e)}
                action={() => sendFile()}
                successAction
                textSuccess="success"
                fileName={fileUpload?.name ?? ''}
              />
            )}
            {deviceIds.length === 1 && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.twinInfo}
                action={() => getTwinInfoAndOpenDialog()}
                successAction={false}
                textSuccess="success"
              />
            )}
            {generation !== newGenerationController && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.proFlag.setProFlag}
                action={() => {
                  openRightSideBarForMethod(viewRightSideBarEnum.setProFlag);
                }}
                successAction={false}
                textSuccess="success"
              />
            )}
            <SideBarSimpleMethodItem
              name={Strings.serverAccess.devices.sendDirectMethodLabel}
              action={() => {
                openRightSideBarForMethod(
                  viewRightSideBarEnum.sendDirectMethodText
                );
              }}
              successAction={false}
              textSuccess="success"
            />
            {isOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.sendProductionMethodLabel}
                action={() => {
                  openRightSideBarForMethod(
                    viewRightSideBarEnum.productionCommands
                  );
                }}
                successAction={false}
                textSuccess="success"
              />
            )}
            {isMultipleControllerOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.sendProductionMethodLabel}
                action={() => {
                  openRightSideBarForMethod(
                    viewRightSideBarEnum.productionCommands
                  );
                }}
                successAction={false}
                textSuccess="success"
              />
            )}
            {!isOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.sendMultipleFiles}
                action={handleSendMultipleFiles}
                successAction={false}
                textSuccess="success"
              />
            )}
            <SideBarSimpleMethodItem
              name={Strings.serverAccess.devices.directFOTA}
              action={() =>
                openRightSideBarForMethod(viewRightSideBarEnum.directFOTA)
              }
              successAction={false}
              textSuccess="success"
            />
            <SideBarSimpleMethodItem
              name={Strings.serverAccess.devices.moveLocation}
              action={() =>
                openRightSideBarForMethod(viewRightSideBarEnum.moveLocation)
              }
              successAction={false}
              textSuccess="success"
            />
            <SideBarSimpleMethodItem
              name={Strings.serverAccess.devices.rebootDevicesLabel}
              action={() => rebootDevices(deviceIds)}
              successAction={false}
              textSuccess="success"
            />
            <SideBarSimpleMethodItem
              name={Strings.serverAccess.devices.setWifiCredentialsLabel}
              action={() => {
                openRightSideBarForMethod(
                  viewRightSideBarEnum.SetWifiCredentials
                );
              }}
              successAction={false}
              textSuccess="success"
            />
            {deviceIds.length === 1 && (
              <SideBarSimpleMethodItem
                name={
                  isLoadingRegenerateIotHubCredentials
                    ? Strings.common.loading
                    : Strings.serverAccess.devices.regenerateIotHubCredentials
                }
                action={() => {
                  regenerateIotHubCredentials({ deviceId: deviceIds[0] });
                }}
                successAction={false}
                textSuccess="success"
                loading={isLoadingRegenerateIotHubCredentials}
              />
            )}
            {deviceIds.length === 1 && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.deleteCredentials}
                action={() => {
                  deleteCredentials(deviceIds[0]);
                }}
                successAction={false}
                textSuccess="success"
                loading={isLoadingDeleteCredentials}
              />
            )}
            {deviceLocationView && deviceIds.length === 1 && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.swapController}
                action={() => setSwapControlelr(true)}
                successAction={false}
                textSuccess="success"
              />
            )}
            <SideBarSimpleMethodItem
              name={Strings.serverAccess.devices.setDeferTime}
              action={() => {
                openRightSideBarForMethod(
                  viewRightSideBarEnum.setDeferEventTime
                );
              }}
              successAction={false}
              textSuccess="success"
            />
            <SideBarSimpleMethodItem
              name={Strings.serverAccess.devices.getDeferTime}
              action={() => {
                openRightSideBarForMethod(
                  viewRightSideBarEnum.getDeferEventTime
                );
              }}
              successAction={false}
              textSuccess="success"
            />
            {deviceLocationView && (
              <SideBarDeleteActionWithPopup
                onDelete={() => deleteFromLocation(macAddress)}
                titleConfirmation={
                  Strings.serverAccess.devices.deleteDeviceFromLocationTitle
                }
                textConfirmation={
                  Strings.serverAccess.devices.deleteDeviceFromLocation
                }
                textIcon={
                  isLoadingDeleteFromLocation
                    ? Strings.common.loading
                    : Strings.devices.deleteAssignedLocation
                }
              />
            )}
            {deviceLocationView && (
              <SideBarDeleteActionWithPopup
                onDelete={() => hardDeleteDevice(macAddress)}
                titleConfirmation={
                  Strings.serverAccess.devices.deleteDeviceFromLocationTitle
                }
                textConfirmation={
                  Strings.serverAccess.devices.deleteDeviceFromLocation
                }
                textIcon={
                  isLoadingDeleteFromLocation
                    ? Strings.common.loading
                    : Strings.devices.deleteServer
                }
              />
            )}
            {!deviceLocationView && (
              <SideBarDeleteActionWithPopup
                onDelete={() => hardDeleteDevice(macAddress)}
                titleConfirmation={
                  Strings.serverAccess.devices.deleteDeviceTitle
                }
                textConfirmation={Strings.serverAccess.devices.deleteDeviceText}
              />
            )}
          </>
        )}
      </Grid>
      <Dialog
        header="Credentials"
        visible={visibleCredentials}
        style={{ width: '65vw', overflow: '999' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        onHide={() => {
          setVisible(false);
          setOpenRightSideBar(false);
        }}
      >
        {isLoadingGetCredentials && <LoadingRowsComponent />}
        {isSuccessGetCredentials &&
          dataGetCredentials &&
          dataGetCredentials.map((item, index) => (
            <JsonViewItem
              key={index}
              title={item.split(' : ')[0]}
              values={item.split(' : ')[1]}
            />
          ))}
      </Dialog>
      <Dialog
        header="Twin Info"
        visible={visibleTwinInfo}
        style={{ width: '65vw' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        onHide={() => {
          setVisibleTwinInfo(false);
          setOpenRightSideBar(false);
        }}
      >
        {isLoadingGetTwinInfo && <LoadingRowsComponent />}
        {isSuccessGetTwinInfo && dataGetTwinInfo && (
          <JsonView
            value={dataGetTwinInfo}
            shortenTextAfterLength={1000}
            displayDataTypes={false}
            displayObjectSize={false}
            indentWidth={2}
          />
        )}
      </Dialog>
    </>
  );
};

DeviceMethodsSideBar.defaultProps = {
  clearSingleDevice: () => {}
};
